import { type PostFormProps } from "@/components/MkPostForm.vue";
import {
    type Component,
    defineAsyncComponent,
    h,
    markRaw,
    ref,
    Slots,
    Text,
} from "vue";
import { v7 } from "uuid";
import MagDialog from "@/components/layout/MagDialog.vue";
import { packed, types } from "magnetar-common";
import { ComponentProps } from "vue-component-type-helpers";
import { RouteLocationRaw } from "vue-router";
import MagDialogWindowView from "@/components/layout/MagDialogWindowView.vue";
import { $tr } from "@/i18n";
import type * as Misskey from "calckey-js";
import { magReactionToLegacy } from "@/scripts-mag/mag-util";
import insertTextAtCursor from "insert-text-at-cursor";
import MagDialogBasicView from "@/components/layout/MagDialogBasicView.vue";
import { placePopover, removePopover } from "@/components/layout/popover-layer";

export const dialogAnimations = ["send", "modal", "leave", "drawer"] as const;

export type DialogProps = {
    id?: string;
    anchor?:
    | "top"
    | "center"
    | "bottom"
    | "left"
    | "right"
    | {
        kind: "around-point";
        x: number;
        y: number;
        align?: "start" | "center" | "end";
        position: "top" | "bottom" | "left" | "right";
        anchorWidth?: number;
        anchorHeight?: number;
    };
    animation?: (typeof dialogAnimations)[number];
    dismissable?: boolean;
    autofocus?: boolean;
    applyStyleToInner?: boolean;
    lowBackdrop?: boolean;
    noBorder?: boolean;
    autoshow?: boolean;
};

export async function placeModal<C extends Component>(
    component: C,
    dispose: Promise<void>,
) {
    const id = v7();

    placePopover({
        kind: "mag-dialog",
        id,
        component: markRaw(component),
    });

    await dispose;

    removePopover(id);
}

export type DialogReceivedProps = {
    onCancel: () => void;
    onClose: () => void;
};

export async function openModal<
    const C extends Component,
    const P extends ComponentProps<C>,
>(
    props: DialogProps,
    component: C,
    componentProps: (receivedProps: DialogReceivedProps) => P,
) {
    const { resolve, promise } = Promise.withResolvers<void>();

    await placeModal(() => {
        return (
            <MagDialog
                {...props}
                onVnodeMounted={(vnode) => {
                    vnode.el?.showModal();
                }}
                onDialogClose={resolve}
            >
                {{
                    default: ({
                        onCancel,
                        onClose,
                        ...otherProps
                    }: {
                        onCancel: () => void;
                        onClose: () => void;
                    } & Record<string, unknown>) => {
                        return h(component, {
                            ...otherProps,
                            ...componentProps({ onClose, onCancel }),
                        });
                    },
                }}
            </MagDialog>
        );
    }, promise);
}

export type WindowProps = {
    title: string;
    fullscreen?: boolean;
    closeButton?: boolean;
    width?: number;
    height?: number;
    padded?: boolean;
};

export async function openModalIn<
    const W extends Component,
    const X extends ComponentProps<W> & {},
    const C extends Component,
    const P extends ComponentProps<C>,
>(
    props: DialogProps,
    wrapper: W,
    wrapperProps: (receivedProps: DialogReceivedProps) => X,
    component: C,
    componentProps: (receivedProps: DialogReceivedProps) => P,
    extraSlots?: Slots,
) {
    const { resolve, promise } = Promise.withResolvers<void>();

    await placeModal(() => {
        return (
            <MagDialog
                {...props}
                onVnodeMounted={(vnode) => {
                    vnode.el?.showModal();
                }}
                onDialogClose={resolve}
            >
                {{
                    default: ({
                        onCancel,
                        onClose,
                        ...otherProps
                    }: {
                        onCancel: () => void;
                        onClose: () => void;
                    } & Record<string, unknown>) => {
                        return h(
                            wrapper,
                            {
                                ...wrapperProps({
                                    onClose,
                                    onCancel,
                                }),
                            },
                            {
                                default: () =>
                                    h(component, {
                                        ...otherProps,
                                        ...componentProps({
                                            onClose,
                                            onCancel,
                                        }),
                                    }),
                                ...(extraSlots ?? {}),
                            },
                        );
                    },
                }}
            </MagDialog>
        );
    }, promise);
}

export async function openModalWindow<
    const C extends Component,
    const P extends ComponentProps<C>,
>(
    props: DialogProps,
    windowProps: WindowProps,
    component: C,
    componentProps: (receivedProps: DialogReceivedProps) => P,
) {
    return await openModalIn(
        props,
        MagDialogWindowView,
        (receivedProps) => ({
            onDwvClose: receivedProps.onClose,
            ...windowProps,
        }),
        component,
        componentProps,
    );
}

export async function openAltTextModal(
    item: packed.PackDriveFileBase | Misskey.entities.DriveFile,
) {
    const { resolve, promise, reject } = Promise.withResolvers<string>();

    const modalClose = openModal(
        {
            dismissable: false,
            noBorder: true,
        },
        defineAsyncComponent({
            loader: () => import("@/components/MkMediaCaption.vue"),
        }),
        ({ onClose, onCancel }) => ({
            title: $tr("describeFile"),
            input: {
                placeholder: $tr("inputNewDescription"),
                default: item.comment || "",
            },
            image: item,
            onDone: (result) => {
                if (result.canceled) {
                    onCancel();
                    return;
                }

                resolve(result.result);
                onClose();
            },
        }),
    );

    modalClose.then(reject);

    return await promise;
}

export async function openDrivePicker<M extends boolean>(
    multiple: M,
    type: "folder" | "file" = "file",
): Promise<Misskey.entities.DriveFile[]> {
    const { resolve, promise, reject } =
        Promise.withResolvers<Misskey.entities.DriveFile[]>();

    const selection = ref<Misskey.entities.DriveFile[]>([]);

    const modalClose = openModalIn(
        {
            dismissable: true,
        },
        MagDialogBasicView,
        (receivedProps) => ({
            onDbvOk: () => {
                resolve(selection.value);
                receivedProps.onClose();
            },
            onDbvCancel: receivedProps.onClose,
            width: 800,
            height: 500,
            disableOk: !selection.value.length,
        }),
        defineAsyncComponent({
            loader: () => import("@/components/MkDrive.vue"),
        }),
        ({ onClose }) => ({
            onCancel: () => onClose(),
            "onChange-selection": (sel) => {
                selection.value = sel;
            },
            multiple,
            select: type,
        }),
        {
            header: () => [
                h(
                    Text,
                    multiple
                        ? type === "file"
                            ? $tr("selectFiles")
                            : $tr("selectFolders")
                        : type === "file"
                            ? $tr("selectFile")
                            : $tr("selectFolder"),
                ),
            ],
            cancel: () => [h(Text, $tr("cancel"))],
            ok: () => [h(Text, $tr("ok"))],
        },
    );

    modalClose.then(reject);

    return await promise;
}

export async function openInstancePicker(): Promise<Misskey.entities.Instance> {
    const { resolve, promise, reject } =
        Promise.withResolvers<Misskey.entities.Instance>();

    const selection = ref<Misskey.entities.Instance | null>(null);

    const modalClose = openModalIn(
        {
            dismissable: true,
        },
        MagDialogBasicView,
        (receivedProps) => ({
            onDbvOk: () => {
                resolve(selection.value!);
                receivedProps.onClose();
            },
            onDbvCancel: receivedProps.onClose,
            width: 800,
            height: 500,
            disableOk: selection.value == null,
        }),
        defineAsyncComponent({
            loader: () => import("@/components/MkInstancePicker.vue"),
        }),
        ({ onClose }) => ({
            onCancel: () => onClose(),
            onSelectionChanged: (sel) => {
                selection.value = sel;
            },
        }),
        {
            header: () => [h(Text, $tr("selectInstance"))],
            cancel: () => [h(Text, $tr("cancel"))],
            ok: () => [h(Text, $tr("ok"))],
        },
    );

    modalClose.then(reject);

    return await promise;
}

export async function openUserPicker(): Promise<packed.PackUserBase> {
    const { resolve, promise, reject } =
        Promise.withResolvers<packed.PackUserBase>();

    const modalClose = openModal(
        {
            dismissable: true,
        },
        defineAsyncComponent({
            loader: () => import("@/components/MagUserPicker.vue"),
        }),
        ({ onClose }) => ({
            onCancel: () => onClose(),
            onOk: (user) => {
                resolve(user);
                onClose();
            },
        }),
    );

    modalClose.then(reject);

    return await promise;
}

export async function openSignUpModal(
    props: DialogProps,
    options?: Partial<WindowProps>,
    innerProps?: {
        autoSet?: boolean;
    },
) {
    const { resolve, promise, reject } = Promise.withResolvers<string | null>();

    const modalClose = openModalWindow(
        {
            dismissable: true,
            ...props,
        },
        {
            title: $tr("signup"),
            padded: true,
            width: 400,
            closeButton: true,
            ...(options ?? {}),
        },
        defineAsyncComponent(() => import("@/components/MkSignup.vue")),
        ({ onClose }) => ({
            onSignup: (res) => {
                onClose();
                resolve(res.i);
            },
            onSignupEmailPending: () => {
                onClose();
                resolve(null);
            },
            autoSet: true,
            ...(innerProps ?? {}),
        }),
    );

    modalClose.then(reject);

    return await promise;
}

export async function openSignInModal(
    props: DialogProps,
    options?: Partial<WindowProps>,
    innerProps?: {
        autoSet?: boolean;
        message?: string;
        withAvatar?: boolean;
    },
) {
    const { resolve, promise, reject } =
        Promise.withResolvers<packed.PackUserSelfMaybeAll>();

    const modalClose = openModalWindow(
        {
            dismissable: true,
            ...props,
        },
        {
            title: $tr("login"),
            padded: true,
            width: 400,
            closeButton: true,
            ...(options ?? {}),
        },
        defineAsyncComponent(() => import("@/components/MkSignin.vue")),
        ({ onClose }) => ({
            onLogin: (res) => {
                onClose();
                resolve(res);
            },
            autoSet: true,
            ...(innerProps ?? {}),
        }),
    );

    modalClose.then(reject);

    return await promise;
}

export async function openModalPage(
    props: DialogProps,
    path: RouteLocationRaw,
) {
    return await openModal(
        props,
        defineAsyncComponent({
            loader: () => import("@/components/layout/MagModalPageView.vue"),
        }),
        ({ onClose }) => ({
            initialPath: path,
            onClose: () => onClose(),
        }),
    );
}

export async function openPostForm(props: PostFormProps) {
    const MkPostForm = defineAsyncComponent(
        () => import("@/components/MkPostForm.vue"),
    );

    return await openModal(
        {
            anchor: "top",
            dismissable: true,
        },
        MkPostForm,
        ({ onCancel, onClose }) => ({
            onPosted: () => onClose(),
            onCancel: () => onCancel(),
            autofocus: true,
            ...props,
        }),
    );
}

export type EmojiPickerProps = {
    showPinned?: boolean;
    asReactionPicker?: boolean;
    maxHeight?: number;
    asDrawer?: boolean;
};

export async function openReactionPicker(
    src?: HTMLElement,
    innerProps?: EmojiPickerProps,
) {
    const { resolve, promise, reject } =
        Promise.withResolvers<types.Reaction>();

    const bounds = src?.getBoundingClientRect();
    const anchor = bounds
        ? ({
            kind: "around-point",
            position: "bottom",
            align: "center",
            x: bounds.x,
            y: bounds.y,
            anchorWidth: bounds.width,
            anchorHeight: bounds.height,
        } as const)
        : undefined;

    let reaction: types.Reaction | null = null;

    const modalClose = openModal(
        {
            dismissable: true,
            applyStyleToInner: true,
            lowBackdrop: true,
            anchor,
        },
        defineAsyncComponent({
            loader: () => import("@/components/MkEmojiPicker.vue"),
        }),
        ({ onClose, onCancel }) => ({
            asReactionPicker: true,
            onChosen: (r) => {
                reaction = r;
                onClose();
            },
            class: innerProps?.asDrawer ? "drawer" : undefined,
            ...innerProps,
        }),
    );

    modalClose.then(() => {
        if (reaction) {
            resolve(reaction);
        } else {
            reject();
        }
    });

    return await promise;
}

export async function openEmojiPicker(
    inputEl: HTMLTextAreaElement | null,
    src?: HTMLElement,
) {
    return await openReactionPicker(src, {
        asReactionPicker: false,
    }).then(async (emoji) => {
        if (inputEl) {
            insertTextAtCursor(inputEl, magReactionToLegacy(emoji) + " ");
        }
    });
}

export type SelectModel<T> = {
    defaultValue?: T;
    current?: T;
} & (
        | {
            items: {
                text: string;
                icon?: string;
                value: T;
            }[];
            groupedItems?: never;
        }
        | {
            items: never;
            groupedItems?: {
                label: string;
                items: {
                    icon?: string;
                    text: string;
                    value: T;
                }[];
            }[];
        }
    );

export async function openSelectModal<const T>(
    props: {
        title?: string;
    } & SelectModel<T>,
): Promise<T> {
    const { resolve, promise, reject } = Promise.withResolvers<T>();

    const modalClose = openModalIn(
        {
            dismissable: true,
            applyStyleToInner: true,
        },
        MagDialogBasicView,
        (receivedProps) => ({
            onDbvCancel: receivedProps.onCancel,
            width: 400,
            height: 500,
        }),
        defineAsyncComponent({
            loader: () => import("@/components/form/MagSelectPanel.vue"),
        }),
        ({ onClose, onCancel }) => ({
            onDone(value) {
                resolve(value as T);
                onClose();
            },
            ...props,
        }),
        {
            header: props.title ? () => [h(Text, props.title)] : undefined,
            cancel: () => [h(Text, $tr("cancel"))],
        },
    );

    modalClose.then(reject);

    return await promise;
}
